var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UXPContext } from '@core/context/Context';
import { useAddOns } from '@helper/AddOn/useAddOns';
import { generateUUID, lookupComponent, hasValue } from '@utils';
import { getWidgetStatus, loadWidgetScript } from './widgetDrawerUtils';
import SearchBox from '@widget/forms/inputs/SearchBox';
import Modal from '@widget/popups/Modal';
import { Loading } from '@widget/loaders';
import useDrawerItems from '@hooks/useDrawerItems';
import useDebounce from '@hooks/useDebounce';
import ButtonComponent from '@widget/buttons/ButtonComponent';
import WidgetDrawerFilters from './WidgetDrawerFilters';
import WidgetDrawerTile from './WidgetDrawerTile';
import { WidgetDrawerType, WidgetDrawerStatus, WidgetDrawerMode } from './WidgetDrawer';
export const WidgetDrawerContent = (props) => {
    var _a, _b, _c;
    const { onChange, existingWidgets, onClose, config } = props;
    const context = useContext(UXPContext);
    const addOns = useAddOns();
    // Config defaults
    const mode = (config === null || config === void 0 ? void 0 : config.mode) || 'multi';
    const showTypeFilter = (_a = config === null || config === void 0 ? void 0 : config.showTypeFilter) !== null && _a !== void 0 ? _a : true;
    const showStatusFilter = (_b = config === null || config === void 0 ? void 0 : config.showStatusFilter) !== null && _b !== void 0 ? _b : true;
    const showCategoryFilter = (_c = config === null || config === void 0 ? void 0 : config.showCategoryFilter) !== null && _c !== void 0 ? _c : true;
    // Calculate allowed statuses (exclude specified ones)
    const allStatuses = [
        WidgetDrawerStatus.All,
        WidgetDrawerStatus.Installed,
        WidgetDrawerStatus.Added,
        WidgetDrawerStatus.NotInstalled
    ];
    const allowedStatuses = allStatuses.filter(status => { var _a; return !((_a = config === null || config === void 0 ? void 0 : config.excludeStatuses) === null || _a === void 0 ? void 0 : _a.includes(status)); });
    // Auto-correct defaultStatus if it's excluded
    const initialStatus = (config === null || config === void 0 ? void 0 : config.defaultStatus) && allowedStatuses.includes(config.defaultStatus)
        ? config.defaultStatus
        : allowedStatuses[0];
    // State
    const [selectedItems, setSelectedItems] = useState([]);
    const [previewInstance, setPreviewInstance] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(0);
    const [selectedType, setSelectedType] = useState((config === null || config === void 0 ? void 0 : config.defaultType) || WidgetDrawerType.Widgets);
    const [selectedStatus, setSelectedStatus] = useState(initialStatus);
    const [selectedCategory, setSelectedCategory] = useState('__all__');
    const [isRefreshing, setIsRefreshing] = useState(false);
    // Dynamic button label based on selected type
    const buttonLabel = useMemo(() => {
        // Priority 1: Type-specific labels
        if (selectedType === WidgetDrawerType.Widgets && (config === null || config === void 0 ? void 0 : config.widgetsButtonLabel)) {
            return config.widgetsButtonLabel;
        }
        if (selectedType === WidgetDrawerType.UIs && (config === null || config === void 0 ? void 0 : config.uisButtonLabel)) {
            return config.uisButtonLabel;
        }
        // Priority 2: Generic label
        if (config === null || config === void 0 ? void 0 : config.buttonLabel) {
            return config.buttonLabel;
        }
        // Priority 3: Default label
        return context.$L('uxp-core.wm.add-to-dashboard');
    }, [selectedType, config, context]);
    const query = useDebounce(searchText, 300);
    const observer = useRef(null);
    // Use unified hook
    const { items, totalCount, filteredCount, isLoading, hasMore, categories, counts, reload } = useDrawerItems({
        searchQuery: query,
        type: selectedType,
        status: selectedStatus,
        category: selectedCategory,
        page,
        existingWidgets,
        enableUIs: config === null || config === void 0 ? void 0 : config.enableUIs
    });
    // Reset page on filter change
    useEffect(() => {
        setPage(0);
    }, [selectedType, selectedStatus, selectedCategory, query]);
    // Intersection observer for infinite scroll
    const lastWidgetRef = useCallback((node) => {
        if (isLoading)
            return;
        if (observer.current)
            observer.current.disconnect();
        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore) {
                setPage((prev) => prev + 1);
            }
        });
        if (node)
            observer.current.observe(node);
    }, [isLoading, hasMore]);
    const handleSave = useCallback(() => {
        onChange({
            selected: selectedItems,
            type: selectedType
        });
        setSelectedItems([]);
        setPreviewInstance(null);
        onClose();
    }, [selectedType, selectedItems, onChange, onClose]);
    const handleDelete = useCallback((item) => {
        onChange({ deleted: [item] });
    }, [onChange]);
    const handleRefresh = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        setIsRefreshing(true);
        yield reload();
        setIsRefreshing(false);
    }), [reload]);
    const getItemName = (item) => item.name || item.label || item.id;
    const handleSelectItem = useCallback((item) => {
        if (mode === WidgetDrawerMode.Single) {
            // Single select mode: select and immediately save
            onChange({
                selected: [item],
                type: selectedType
            });
            onClose();
        }
        else {
            // Multi-select mode: toggle selection
            setSelectedItems((prev) => {
                const itemName = getItemName(item);
                const index = prev.findIndex((s) => s.id === item.id && getItemName(s) === itemName);
                if (index !== -1) {
                    return prev.filter((_, i) => i !== index);
                }
                return [...prev, item];
            });
        }
    }, [mode, selectedType, onChange, onClose]);
    const isItemSelected = useCallback((item) => {
        const itemName = getItemName(item);
        return selectedItems.some((s) => s.id === item.id && getItemName(s) === itemName);
    }, [selectedItems]);
    const handleOpenPreview = useCallback((widget) => __awaiter(void 0, void 0, void 0, function* () {
        if (!widget)
            return;
        const registered = lookupComponent(widget.id);
        if (registered) {
            setPreviewInstance(widget);
        }
        else {
            const loaded = yield loadWidgetScript(widget);
            if (loaded)
                setPreviewInstance(widget);
        }
    }), []);
    const handleClosePreview = useCallback(() => {
        setPreviewInstance(null);
    }, []);
    const renderItems = () => (_jsx("div", { className: "uxpcore_widgetdrawer__items", id: "uxpcore_widgetdrawer__list-container", children: items.map((item, index) => {
            const isWidget = selectedType === WidgetDrawerType.Widgets;
            const isSelected = isItemSelected(item);
            const isLastItem = index === items.length - 1;
            const status = isWidget ? getWidgetStatus(item, existingWidgets, addOns) : undefined;
            return (_jsx(WidgetDrawerTile, { ref: isLastItem ? lastWidgetRef : null, item: item, type: isWidget ? 'widget' : 'ui', isSelected: isSelected, isMultiSelect: mode === WidgetDrawerMode.Multiple, onSelect: () => handleSelectItem(item), status: status, onPreview: isWidget ? () => handleOpenPreview(item) : undefined, onRemove: status === 'added' ? () => handleDelete(item) : undefined, context: context, addOns: addOns }, `${item.id}-${index}`));
        }) }));
    const renderPreview = () => {
        var _a, _b, _c, _d;
        if (!previewInstance)
            return null;
        const registered = lookupComponent(previewInstance === null || previewInstance === void 0 ? void 0 : previewInstance.id);
        if (!registered) {
            return (_jsx(Modal, { show: !!previewInstance, onClose: handleClosePreview, className: "uxpcore_widgetdrawer__preview-modal", title: context.$L('uxp-core.text.widget-preview'), autoSize: true, children: _jsx(Loading, {}) }));
        }
        const Component = registered.component;
        let props = {
            uxpContext: context,
            instanceId: generateUUID(),
            isPreview: true
        };
        let layout = ((_a = registered.configs) === null || _a === void 0 ? void 0 : _a.layout) || { w: 6, h: 8 };
        if ((_b = previewInstance.props) === null || _b === void 0 ? void 0 : _b.__template__) {
            layout = previewInstance.props.__template__.layout || layout;
            props.uiProps = ((_c = previewInstance.props) === null || _c === void 0 ? void 0 : _c.uiProps) || {};
        }
        const { w, h } = layout;
        const cellWidth = 1200 / 30;
        const wW = w * cellWidth;
        const wH = h * cellWidth;
        const scaleTo = Math.min(320 / wW, 320 / wH) - 0.01;
        const containerStyles = {
            width: wW,
            height: wH,
            scale: `${scaleTo}`
        };
        return (_jsx(Modal, { show: !!previewInstance, onClose: handleClosePreview, className: "uxpcore_widgetdrawer__preview-modal", title: context.$L('uxp-core.text.widget-preview'), autoSize: true, children: _jsxs("div", { className: "uxpcore_widgetdrawer__preview-container", children: [_jsx("div", { className: "uxpcore_widgetdrawer__preview-widget", style: { width: 350, height: 350 }, children: _jsx("div", { className: "uxpcore_widgetdrawer__preview-inner", style: containerStyles, children: _jsx(Component, Object.assign({}, props)) }) }), _jsxs("div", { className: "uxpcore_widgetdrawer__preview-details", children: [_jsxs("div", { className: "uxpcore_widgetdrawer__preview-info", children: [_jsx("div", { className: "uxpcore_widgetdrawer__preview-name", children: context.$L(previewInstance === null || previewInstance === void 0 ? void 0 : previewInstance.name) }), _jsx("div", { className: "uxpcore_widgetdrawer__preview-description", children: hasValue(previewInstance === null || previewInstance === void 0 ? void 0 : previewInstance.description)
                                            ? context.$L(previewInstance === null || previewInstance === void 0 ? void 0 : previewInstance.description)
                                            : context.$L('uxp-core.text.no-description') }), ((_d = previewInstance === null || previewInstance === void 0 ? void 0 : previewInstance.tags) === null || _d === void 0 ? void 0 : _d.length) > 0 && (_jsx("div", { className: "uxpcore_widgetdrawer__preview-tags", children: previewInstance.tags.map((t, i) => (_jsx("div", { className: "uxpcore_widgetdrawer__preview-tag", children: context.$L(t) }, i))) }))] }), _jsx("div", { className: "uxpcore_widgetdrawer__preview-actions", children: _jsx(ButtonComponent, { leftIcon: 'fal plus', title: context.$L('uxp-core.text.add-widget'), onClick: () => {
                                        onChange({ selected: [previewInstance] });
                                        handleClosePreview();
                                        onClose();
                                    } }) })] })] }) }));
    };
    return (_jsxs("div", { className: "uxpcore_widgetdrawer__container", children: [_jsx(WidgetDrawerFilters, { selectedType: selectedType, selectedStatus: selectedStatus, selectedCategory: selectedCategory, categories: categories, allowedStatuses: allowedStatuses, counts: counts, showTypeFilter: showTypeFilter, showStatusFilter: showStatusFilter, showCategoryFilter: showCategoryFilter, onTypeChange: setSelectedType, onStatusChange: setSelectedStatus, onCategoryChange: setSelectedCategory }), _jsxs("div", { className: "uxpcore_widgetdrawer__body", children: [_jsxs("div", { className: "uxpcore_widgetdrawer__header", children: [_jsx("div", { className: "uxpcore_widgetdrawer__header-title", children: (config === null || config === void 0 ? void 0 : config.title) || context.$L('uxp-core.wm.title') }), _jsx("div", { className: "uxpcore_widgetdrawer__closebutton", children: _jsx(ButtonComponent, { leftIcon: 'fal times', onClick: onClose, variant: 'secondary' }) })] }), _jsxs("div", { className: "uxpcore_widgetdrawer__toolbar", children: [_jsxs("div", { className: "uxpcore_widgetdrawer__toolbar-left", children: [_jsx(SearchBox, { value: searchText, onChange: setSearchText }), _jsx(ButtonComponent, { leftIcon: isRefreshing ? 'fal spinner-third fa-spin' : 'fal sync', iconOnly: true, onClick: handleRefresh, disabled: isRefreshing, variant: 'secondary', title: context.$L('uxp-core.text.refresh') })] }), _jsx("div", { className: "uxpcore_widgetdrawer__toolbar-right", children: mode === 'multi' && (_jsxs(_Fragment, { children: [selectedItems.length > 0 && (_jsx(ButtonComponent, { leftIcon: 'fal times', title: context.$L('uxp-core.text.clear'), onClick: () => setSelectedItems([]), variant: 'secondary' })), _jsx(ButtonComponent, { leftIcon: 'fal plus', title: buttonLabel, onClick: handleSave, active: selectedItems.length > 0, disabled: selectedItems.length === 0, variant: 'primary' })] })) })] }), _jsxs("div", { className: "uxpcore_widgetdrawer__counts", children: [mode === 'multi' && selectedItems.length > 0 && (_jsx("div", { className: "uxpcore_widgetdrawer__count-item", children: context.$L('uxp-core.text.selected', { count: selectedItems.length }) })), _jsx("div", { className: "uxpcore_widgetdrawer__count-item", children: context.$L('uxp-core.text.showing-count', {
                                    count: items.length,
                                    total: filteredCount
                                }) })] }), _jsx("div", { className: "uxpcore_widgetdrawer__content", children: _jsxs("div", { className: "uxpcore_widgetdrawer__content-body", children: [renderItems(), isLoading ? (_jsx("div", { className: "uxpcore_widgetdrawer__loading", children: _jsx(Loading, {}) })) : (_jsxs("div", { className: "uxpcore_widgetdrawer__notification", children: [!hasMore && items.length > 0 && (_jsxs("div", { className: "uxpcore_widgetdrawer__end-of-content", children: ["-- ", context.$L('uxp-core.text.end-of-content'), " --"] })), items.length === 0 && (_jsx("div", { className: "uxpcore_widgetdrawer__no-widgets", children: context.$L(selectedType === WidgetDrawerType.UIs
                                                ? 'uxp-core.text.no-uis-found'
                                                : 'uxp-core.text.no-widgets-found') }))] }))] }) })] }), renderPreview()] }));
};
export default WidgetDrawerContent;
